/**
 * PHARMAKON — BETA mode flag management
 *
 * The "Try Before Purchase" demo experience. When active, the entire frontend
 * runs against an in-memory mock backend (see ./adapter.js + ./router.js) —
 * no network calls leave the browser, no real auth, no real DB.
 *
 * Entry: LoginPage's three BETA buttons call `enterBeta(role)` which seeds
 * the in-memory store, plants a fake auth token + user, then navigates the
 * SPA to /dashboard. Exit: the user logging out clears the flag.
 */
import { seedStore, resetStore } from "./store.js";
import { getDemoUser } from "./data/users.js";

const FLAG_KEY = "pharmakon_beta_mode";
const ROLE_KEY = "pharmakon_beta_role";
const TOKEN = "BETA_DEMO_TOKEN";

/** Whether the SPA is currently in BETA / demo mode. */
export function isBetaMode() {
  try {
    return localStorage.getItem(FLAG_KEY) === "true";
  } catch {
    return false;
  }
}

/** Which demo role is currently active (PROPRIETAIRE / MANAGER / CAISSIER). */
export function getBetaRole() {
  try {
    return localStorage.getItem(ROLE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Enter BETA mode as the requested role. Seeds the in-memory store with a
 * rich demo dataset, plants a fake JWT + user in localStorage (so existing
 * authStore / api.client logic sees a logged-in user), and returns the
 * synthetic user object for the caller to push into the auth store.
 */
export function enterBeta(role) {
  if (!["PROPRIETAIRE", "MANAGER", "CAISSIER"].includes(role)) {
    throw new Error(`Unknown BETA role: ${role}`);
  }
  // Seed the store FIRST so the auth handler can look the demo user up.
  seedStore(role);
  try {
    localStorage.setItem(FLAG_KEY, "true");
    localStorage.setItem(ROLE_KEY, role);
    localStorage.setItem("pharmakon_token", TOKEN);
    localStorage.setItem("pharmakon_refresh", TOKEN);
  } catch {
    /* private mode, silently swallow */
  }

  const user = getDemoUser(role);
  try {
    localStorage.setItem("pharmakon_user", JSON.stringify(user));
  } catch {
    /* ignore */
  }
  return user;
}

/** Leave BETA mode: clear the flag, the fake auth, and the in-memory store. */
export function exitBeta() {
  try {
    localStorage.removeItem(FLAG_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem("pharmakon_token");
    localStorage.removeItem("pharmakon_refresh");
    localStorage.removeItem("pharmakon_user");
  } catch {
    /* ignore */
  }
  resetStore();
}
